/**
 * Contained stage-asset viewer for Object Image Processing chain rows.
 *
 * Replaces antd Image's immersive fullscreen preview (which painted the
 * checkerboard across the entire browser viewport) with a modal that keeps
 * Vistoera page context, confines checkerboard to the image viewport, and
 * opens on a display-tier URL (proxy / transparent_proxy / thumb) — never
 * the multi-MB full transparent PNG.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Modal, Space, Tooltip } from 'antd'
import {
  ArrowLeftOutlined,
  CloseOutlined,
  DownloadOutlined,
  MinusOutlined,
  PlusOutlined,
  ExpandOutlined,
} from '@ant-design/icons'
import { MODAL_SURFACE_CLASS, useModalScrollLock } from '../saas/utils/scrollLock'

export type StageViewerKind = 'original' | 'transparent' | 'background' | 'shadow'

interface Props {
  open: boolean
  title: string
  kind: StageViewerKind
  /** Display-tier URL (proxy / transparent_proxy / stage thumb). */
  displayUrl: string
  /** Optional full-resolution URL — only fetched when the user asks. */
  fullUrl?: string | null
  downloadUrl?: string | null
  onClose: () => void
}

type ZoomMode = 'fit' | 'pct'

export default function StageAssetViewer({
  open,
  title,
  kind,
  displayUrl,
  fullUrl,
  downloadUrl,
  onClose,
}: Props) {
  const [src, setSrc] = useState(displayUrl)
  const [mode, setMode] = useState<ZoomMode>('fit')
  const [pct, setPct] = useState(100)
  const [fullRequested, setFullRequested] = useState(false)
  const stageRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{ x: number; y: number; sl: number; st: number } | null>(null)

  useEffect(() => {
    if (!open) return
    setSrc(displayUrl)
    setMode('fit')
    setPct(100)
    setFullRequested(false)
  }, [open, displayUrl])

  // Modal Surface Isolation Layer 1 (VISTOERA_OBJECT_IMAGE_VIEWER_EDITOR_
  // FULL_MODAL_ISOLATION_P0): background page must not scroll/jitter while
  // this viewer is open, from the instant it opens (before the image has
  // loaded) through close. See `MODAL_SURFACE_CLASS` on the `Modal` below.
  useModalScrollLock(open)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const requestFull = useCallback(() => {
    if (!fullUrl || fullRequested) return
    setFullRequested(true)
    setSrc(fullUrl)
  }, [fullUrl, fullRequested])

  const zoomIn = () => {
    setMode('pct')
    setPct((p) => Math.min(400, p + 25))
  }
  const zoomOut = () => {
    setMode('pct')
    setPct((p) => Math.max(25, p - 25))
  }
  const fit = () => {
    setMode('fit')
    setPct(100)
    if (stageRef.current) {
      stageRef.current.scrollLeft = 0
      stageRef.current.scrollTop = 0
    }
  }
  const actualSize = () => {
    setMode('pct')
    setPct(100)
    // 100% is CSS natural size of the *currently loaded* display asset.
    // Do not auto-upgrade to full-resolution solely because the user picked 100%.
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if (mode !== 'pct' || !stageRef.current) return
    stageRef.current.setPointerCapture(e.pointerId)
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      sl: stageRef.current.scrollLeft,
      st: stageRef.current.scrollTop,
    }
  }
  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current
    if (!d || !stageRef.current) return
    stageRef.current.scrollLeft = d.sl - (e.clientX - d.x)
    stageRef.current.scrollTop = d.st - (e.clientY - d.y)
  }
  const endDrag = () => {
    dragRef.current = null
  }

  const checker = kind === 'transparent'

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      width={1280}
      style={{ maxWidth: 'min(92vw, 1280px)' }}
      destroyOnClose
      maskClosable
      className="stage-asset-viewer-modal"
      wrapClassName={MODAL_SURFACE_CLASS}
      styles={{ body: { padding: 0 }, mask: { backgroundColor: 'transparent' } }}
      data-testid="stage-asset-viewer"
    >
      <div className="sav-shell">
        <header className="sav-header">
          <div className="sav-header-left">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={onClose}
              data-testid="sav-back"
            >
              返回
            </Button>
            <span className="sav-title" title={title}>
              {title}
            </span>
          </div>
          <Space size={4} className="sav-toolbar" wrap>
            <Tooltip title="适应窗口">
              <Button size="small" icon={<ExpandOutlined />} onClick={fit} data-testid="sav-fit">
                适应窗口
              </Button>
            </Tooltip>
            <Button size="small" onClick={actualSize} data-testid="sav-100">
              100%
            </Button>
            <Button size="small" icon={<MinusOutlined />} onClick={zoomOut} data-testid="sav-zoom-out" />
            <span className="sav-zoom-label">{mode === 'fit' ? '适应' : `${pct}%`}</span>
            <Button size="small" icon={<PlusOutlined />} onClick={zoomIn} data-testid="sav-zoom-in" />
            {fullUrl && !fullRequested && (
              <Button size="small" onClick={requestFull} data-testid="sav-load-full">
                查看原始尺寸
              </Button>
            )}
            {downloadUrl && (
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => {
                  const a = document.createElement('a')
                  a.href = downloadUrl
                  a.rel = 'noopener'
                  a.click()
                }}
                data-testid="sav-download"
              >
                下载
              </Button>
            )}
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={onClose}
              aria-label="关闭"
              data-testid="sav-close"
            />
          </Space>
        </header>
        <div
          ref={stageRef}
          className={`sav-stage${checker ? ' is-transparent' : ''}`}
          data-testid="sav-stage"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          {src ? (
            <img
              src={src}
              alt={title}
              className={mode === 'fit' ? 'sav-img-fit' : 'sav-img-pct'}
              style={mode === 'pct' ? { width: `${pct}%`, maxWidth: 'none', height: 'auto' } : undefined}
              draggable={false}
              data-testid="sav-image"
            />
          ) : null}
        </div>
      </div>
    </Modal>
  )
}
